
/*
 * ----------------------------------------------------------------------------------
 * Imports - External
 * ----------------------------------------------------------------------------------
 */
import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';
import {
    AuthenticatedUserContext,
    Checkbox,
    CollaboratingGroupContext,
    DatePicker,
    DefaultFooterPosition,
    FormBreadcrumbs,
    GroupedField,
    GroupedFields,
    InputOptionMode,
    InstitutionContext,
    MasterGroupContext,
    PageLayout,
    PatientContext,
    RadioGroup,
    RouteLoading,
    Text,
    ToggleButtonGroup,
    UserType
} from '@ngt/opms';
import { Permission, usePermissionsByIds } from '@ngt/opms-bctapi';
import React, { FC, FunctionComponent, useContext } from 'react';

/*
 * ----------------------------------------------------------------------------------
 * Imports - Internal
 * ----------------------------------------------------------------------------------
 */
import { ValidateOn } from '@ngt/forms-core';
import { AppSettings } from '../../../AppSettings';
import SourceDocuments from '../../documents/SourceDocuments';
import TranslatedFooter from './templates/TranslatedFooter';
import { TranslatedProForm } from './TranslatedProForm';


/*
 * ----------------------------------------------------------------------------------
 * Interface
 * ----------------------------------------------------------------------------------
 */

interface IBriefIllnessPerceptionQuestionnaireFormProps { }

interface IMobileRadioGroupProps { 
    propertyPath: string;
    lowLabel?: string;
    highLabel?: string;
}

interface IWebRadioGroupProps {
    propertyPath: string;
    lowLabel?: string;
    highLabel?: string;
}

/*
 * ---------------------------------------------------------------------------------
 * Styles
 * ---------------------------------------------------------------------------------
 */


/*
 * ---------------------------------------------------------------------------------
 * Functions
 * ---------------------------------------------------------------------------------
 */

/*
 * ----------------------------------------------------------------------------------
 * Components
 * ----------------------------------------------------------------------------------
 */

const MobileRadioGroup: FC<IMobileRadioGroupProps> = ({
    propertyPath,
    lowLabel,
    highLabel
}) => {
    return (
        <GroupedFields>
            <Box 
                sx={{
                    display: 'grid',
                    width: '100%',
                    gridTemplateColumns: '200px 1fr'
                }}
            >
                <Box
                    sx={{ 
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        height: '100%',
                        padding: '64px 0 16px 0'
                    }}
                >
                    <Box maxWidth='100px'>
                        <Typography sx={{ textAlign: 'center' }}>{lowLabel ? lowLabel : <>No affect at all</>}</Typography>
                    </Box>
                    <Box maxWidth='100px'>
                        <Typography sx={{ textAlign: 'center' }}>{highLabel ? highLabel : <>Severely affects my life</> }</Typography>
                    </Box>
                </Box>
                <Box>
                    <GroupedField
                        propertyPath={propertyPath}
                        component={ToggleButtonGroup}
                        orientation="vertical"
                        spacing={3}
                        FieldGridProps={{
                            size: {
                                xs: 12,
                                sm: 12,
                                md: 12,
                                lg: 12,
                                xl: 12
                            }
                        }}
                    />
                </Box>
            </Box>
        </GroupedFields>
    );
}

const WebRadioGroup: FC<IWebRadioGroupProps> = ({
    propertyPath,
    lowLabel,
    highLabel
}) => {
    return (
        <GroupedFields>
            <GroupedField
                propertyPath={propertyPath}
                component={RadioGroup}
                style={{ 
                    width: '100%', 
                    display: 'flex',
                    justifyContent: 'space-between'
                }}
                FieldGridProps={{
                    size: {
                        xs: 12,
                        sm: 12,
                        md: 12,
                        lg: 12,
                        xl: 12
                    }
                }}
                row
            />
            <Box
                sx={{ 
                    display: 'flex',
                    justifyContent: 'space-between',
                    padding: '8px 24px'
                }}
            >
                <Box maxWidth='100px'>
                    <Typography sx={{ textAlign: 'center' }}>{lowLabel ? lowLabel : <>No affect at all</>}</Typography>
                </Box>
                <Box maxWidth='100px'>
                    <Typography sx={{ textAlign: 'center' }}>{highLabel ? highLabel : <>Severely affects my life</>}</Typography>
                </Box>
            </Box>
        </GroupedFields>
    );
}

const BriefIllnessPerceptionQuestionnaireForm: FunctionComponent<IBriefIllnessPerceptionQuestionnaireFormProps> = () => {
    const theme = useTheme();

    const { data: user } = useContext(AuthenticatedUserContext);
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const isPatient = React.useMemo(() => {
        return user?.type === UserType.Patient;
    }, [user]);

    const { data: masterGroup } = useContext(MasterGroupContext);
    const { data: collaboratingGroup } = useContext(CollaboratingGroupContext);
    const { data: institution } = useContext(InstitutionContext);
    const { data: patient } = useContext(PatientContext);

    const {
        permissions: [
            canUpdatePro, 
            canAdministerPro
        ],
        loading: permissionsLoading
    } = usePermissionsByIds(
        [
            Permission.OpmsProUpdate,
            Permission.OpmsProAdminister
        ], 
        masterGroup?.id, 
        collaboratingGroup?.id, 
        institution?.id, 
        patient?.id
    );

    const showSteps = isPatient || isMobile;

    if (permissionsLoading) {
        return (
            <RouteLoading />
        );
    }

    return (
        <PageLayout
            breadcrumbs={<FormBreadcrumbs />}
        >
            <TranslatedProForm
                formTranslationKey={"forms.briefIllnessPerceptionQuestionnaire"}
                validateOn={ValidateOn.onChange}
                useSteps={showSteps}
                readOnly={showSteps ? !canUpdatePro : !canAdministerPro}
                FooterOptions={{
                    content: <TranslatedFooter isPatient={isPatient} />,
                    position: DefaultFooterPosition
                }}
                DialogOptions={AppSettings.proForms.dialogOptions}
                showEventHeading
                debug={AppSettings.debug}
            >
                {
                    !isPatient && (
                        <GroupedFields>
                            <GroupedField
                                        propertyPath="dateCompleted"
                                component={DatePicker}
                                FieldGridProps={{
                                    size: {
                                        xs: 12,
                                        sm: 12,
                                        md: showSteps ? 12 : 6,
                                        lg: showSteps ? 12 : 5,
                                        xl: showSteps ? 12 : 5
                                    }
                                }}
                            />
                            <GroupedField
                                        LabelProps={{
                                    label: <></>
                                }}
                                formInputOptions={{
                                    labelMode: InputOptionMode.Auto
                                }}
                                propertyPath="ptDidNotComplete"
                                component={Checkbox}
                                FieldGridProps={{
                                    size: {
                                        xs: 12,
                                        sm: 12,
                                        md: showSteps ? 12 : 6,
                                        lg: showSteps ? 12 : 5,
                                        xl: showSteps ? 12 : 5
                                    }
                                }}
                            />
                        </GroupedFields>
                    )
                }
                {
                    showSteps ?
                        <MobileRadioGroup 
                            propertyPath="affectOnLifeScore"
                            lowLabel='No affect at all'
                            highLabel='Severely affects my life'
                        /> :
                        <WebRadioGroup 
                            propertyPath="affectOnLifeScore"
                            lowLabel='No affect at all'
                            highLabel='Severely affects my life'
                        />
                }
                {
                    showSteps ?
                        <MobileRadioGroup 
                            propertyPath="thoughtsOnCancerDurationScore"
                            lowLabel='A very short time'
                            highLabel='Forever'
                        /> :
                        <WebRadioGroup 
                            propertyPath="thoughtsOnCancerDurationScore"
                            lowLabel='A very short time'
                            highLabel='Forever'
                        />
                }
                {
                    showSteps ?
                        <MobileRadioGroup 
                            propertyPath="controlOverCancerScore"
                            lowLabel='Absolutely no control'
                            highLabel='Extreme control'
                        /> :
                        <WebRadioGroup 
                            propertyPath="controlOverCancerScore"
                            lowLabel='Absolutely no control'
                            highLabel='Extreme control'
                        />
                }
                {
                    showSteps ?
                        <MobileRadioGroup 
                            propertyPath="benefitOfTreatmentScore"
                            lowLabel='Not at all'
                            highLabel='Extremely helpful'
                        /> :
                        <WebRadioGroup 
                            propertyPath="benefitOfTreatmentScore"
                            lowLabel='Not at all'
                            highLabel='Extremely helpful'
                        />
                }
                {
                    showSteps ?
                        <MobileRadioGroup 
                            propertyPath="symptomsExperiencedFrequencyScore"
                            lowLabel='No symptoms at all'
                            highLabel='Many severe symptoms'
                        /> :
                        <WebRadioGroup 
                            propertyPath="symptomsExperiencedFrequencyScore"
                            lowLabel='No symptoms at all'
                            highLabel='Many severe symptoms'
                        />
                }
                {
                    showSteps ?
                        <MobileRadioGroup 
                            propertyPath="levelOfConcernScore"
                            lowLabel='Not at all concerned'
                            highLabel='Extremely concerned'
                        /> :
                        <WebRadioGroup 
                            propertyPath="levelOfConcernScore"
                            lowLabel='Not at all concerned'
                            highLabel='Extremely concerned'
                        />
                }
                {
                    showSteps ?
                        <MobileRadioGroup 
                            propertyPath="confidenceInUnderstandingCancerScore"
                            lowLabel='Don’t understand at all'
                            highLabel='Understand very clearly'
                        /> :
                        <WebRadioGroup 
                            propertyPath="confidenceInUnderstandingCancerScore"
                            lowLabel='Don’t understand at all'
                            highLabel='Understand very clearly'
                        />
                }
                {
                    showSteps ?
                        <MobileRadioGroup 
                            propertyPath="emotionalEffectScore"
                            lowLabel='Not at all affected emotionally'
                            highLabel='Extremely affected emotionally'
                        /> :
                        <WebRadioGroup 
                            propertyPath="emotionalEffectScore"
                        />
                }
                <GroupedFields>
                    <Typography sx={{p: 2}}>
                        Please list in rank-order the three most important factors that you believe caused your
                        breast cancer.
                    </Typography>
                    <Typography  sx={{p: 2}}>
                        The most important causes for me:
                    </Typography>
                    <GroupedField
                        propertyPath="believedCancerFactor1"
                        component={Text}
                        FieldGridProps={{
                            size: {
                                xs: 11,
                                sm: 11,
                                md: 11,
                                lg: 11,
                                xl: 11
                            }
                        }}
                    />
                    <GroupedField
                        propertyPath="believedCancerFactor2"
                        component={Text}
                        FieldGridProps={{
                            size: {
                                xs: 11,
                                sm: 11,
                                md: 11,
                                lg: 11,
                                xl: 11
                            }
                        }}
                    />
                    <GroupedField
                        propertyPath="believedCancerFactor3"
                        component={Text}
                        FieldGridProps={{
                            size: {
                                xs: 11,
                                sm: 11,
                                md: 11,
                                lg: 11,
                                xl: 11
                            }
                        }}
                    />
                </GroupedFields>
                {
                    canAdministerPro && (
                        <SourceDocuments />
                    )
                }
            </TranslatedProForm>
        </PageLayout>
    );
}


/*
 * ----------------------------------------------------------------------------------
 * Default Export
 * ----------------------------------------------------------------------------------
 */

export default BriefIllnessPerceptionQuestionnaireForm;
